import {
  Outlet,
  createFileRoute,
  redirect,
  useParams,
} from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Suspense } from 'react';

import { NotFound } from '@/components/Error/NotFound';
import { VStack } from '@/components/Layout';

import { getEmployeeLatestPaySlipPeriodFromDb } from '../-api';
import { EmployeePaySlipDetailSearchSchema } from '../-zod-schema';

import { EmployeePaySlipDetailHeader } from './-components/EmployeePaySlipDetailHeader';
import { useEmployeePaySlipProfile } from './-query';

export const Route = createFileRoute(
  '/_staff/payroll/_layout/pay-slips/$employeeId',
)({
  validateSearch: zodValidator(EmployeePaySlipDetailSearchSchema),
  beforeLoad: async ({ params, search }) => {
    const y = search.year;
    const m = search.month;
    if (y != null && m != null) return;

    const latest = await getEmployeeLatestPaySlipPeriodFromDb(
      params.employeeId,
    );
    if (!latest) return;

    throw redirect({
      to: '/payroll/pay-slips/$employeeId',
      params: { employeeId: params.employeeId },
      search: { year: latest.year, month: latest.month },
      replace: true,
    });
  },
  component: EmployeePaySlipEmployeeDetailShell,
});

function EmployeePaySlipEmployeeDetailShell() {
  const { employeeId } = useParams({
    from: '/_staff/payroll/_layout/pay-slips/$employeeId',
  });
  const { data: profile, isPending } = useEmployeePaySlipProfile(employeeId);

  if (isPending) {
    return (
      <VStack className="h-full min-h-0 min-w-0 flex-1">
        <div className="border-b border-neutral-muted-border">
          <VStack className="gap-3 bg-neutral-fill px-12 pt-4 shadow-sm">
            <div className="h-4 w-64 animate-pulse rounded bg-neutral-muted-fill" />
            <div className="h-10 w-96 animate-pulse rounded bg-neutral-muted-fill" />
            <div className="h-9 w-full max-w-2xl animate-pulse rounded bg-neutral-muted-fill" />
          </VStack>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-auto px-12 pt-6">
          <div className="h-48 w-full animate-pulse rounded-lg bg-neutral-muted-fill" />
        </div>
      </VStack>
    );
  }

  if (!profile) {
    return <NotFound />;
  }

  return (
    <VStack className="h-full min-h-0 min-w-0 flex-1">
      <EmployeePaySlipDetailHeader profile={profile} />
      <Suspense
        fallback={
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto px-12 py-6">
            <div className="h-48 w-full animate-pulse rounded-lg bg-neutral-muted-fill" />
          </div>
        }
      >
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto">
          <Outlet />
        </div>
      </Suspense>
    </VStack>
  );
}
